import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import RawHeader from "../components/RawHeader";
import AdminNav from "../components/AdminNav";

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    api.get("/admin/orders")
      .then((r) => setOrders(r.data))
      .finally(() => setLoading(false));
  }, []);

  const paidOrders = orders.filter((o) => o.payment_status === "paid");
  const totalRev = paidOrders.reduce((a, o) => a + (o.amount_total ?? o.amount ?? 0), 0);
  const totalVat = paidOrders.reduce((a, o) => a + (o.amount_vat ?? 0), 0);

  const q = query.trim().toLowerCase();
  const filteredOrders = orders.filter((o) => {
    if (statusFilter === "paid" && o.payment_status !== "paid") return false;
    if (statusFilter === "pending" && o.payment_status === "paid") return false;
    if (!q) return true;
    const itemsStr = Array.isArray(o.items)
      ? o.items.map((it) => it.product_name).join(" ")
      : "";
    const hay = [
      o.customer_email,
      o.shipping_name,
      o.customer_phone,
      o.product_name,
      itemsStr,
      o.session_id,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });

  return (
    <div className="min-h-screen text-white">
      <RawHeader variant="admin" />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-8">
        <AdminNav />
        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">// TRANSACTION_LOG</div>
        <h1 className="font-display text-3xl sm:text-4xl mb-8">ORDERS</h1>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800 mb-8">
          <div className="bg-[#050505] p-5">
            <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 mb-2">// TOTAL</div>
            <div className="font-display text-2xl sm:text-3xl">{orders.length}</div>
          </div>
          <div className="bg-[#050505] p-5">
            <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 mb-2">// PAID</div>
            <div className="font-display text-2xl sm:text-3xl text-[#16a34a]">{paidOrders.length}</div>
          </div>
          <div className="bg-[#050505] p-5">
            <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 mb-2">// REVENUE</div>
            <div className="font-display text-2xl sm:text-3xl text-[#d4af37]">£{totalRev.toFixed(2)}</div>
          </div>
          <div className="bg-[#050505] p-5">
            <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 mb-2">// VAT OWED</div>
            <div className="font-display text-2xl sm:text-3xl text-zinc-300">£{totalVat.toFixed(2)}</div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20 text-zinc-600 text-xs tracking-[0.3em] uppercase">LOADING...</div>
        ) : orders.length === 0 ? (
          <div className="border border-dashed border-zinc-800 p-16 text-center">
            <div className="text-zinc-500 text-xs tracking-[0.3em] uppercase">// NO ORDERS YET</div>
          </div>
        ) : (
          <>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4" data-testid="orders-filters">
            <input
              data-testid="orders-search"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by email, name, product, session id…"
              className="flex-1 bg-[#050505] border border-zinc-800 focus:border-[#d4af37] outline-none px-4 py-2.5 text-sm font-mono text-white placeholder:text-zinc-600"
            />
            <div className="flex gap-2">
              {[
                ["all", "ALL"],
                ["paid", "PAID"],
                ["pending", "PENDING"],
              ].map(([val, label]) => (
                <button
                  key={val}
                  data-testid={`filter-${val}`}
                  onClick={() => setStatusFilter(val)}
                  className={`px-3 py-2 text-[10px] tracking-[0.25em] uppercase border ${
                    statusFilter === val
                      ? "border-[#d4af37] text-[#d4af37] bg-[#d4af37]/10"
                      : "border-zinc-700 text-zinc-500 hover:text-white hover:border-zinc-500"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-600 mb-3" data-testid="orders-count">
            // SHOWING {filteredOrders.length} OF {orders.length}
          </div>

          <div className="border border-zinc-800 overflow-x-auto" data-testid="orders-table">
            <table className="w-full font-mono text-sm">
              <thead>
                <tr className="border-b border-zinc-800 bg-[#0a0a0a]">
                  {["DATE", "PRODUCT", "SIZE", "QTY", "TOTAL", "STATUS", "CUSTOMER", ""].map((h) => (
                    <th key={h} className="text-left p-3 text-[9px] tracking-[0.25em] uppercase text-zinc-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((o) => (
                  <React.Fragment key={o.session_id}>
                    <tr className="border-b border-zinc-800/50 hover:bg-[#0a0a0a]">
                      <td className="p-3 text-zinc-500 text-xs">{o.created_at?.slice(0, 16).replace("T", " ")}</td>
                      <td className="p-3">
                        {o.multi_line && Array.isArray(o.items) ? (
                          <div>
                            <div className="text-[#d4af37] text-[10px] tracking-[0.2em] uppercase">{o.items.length} items</div>
                            <div className="text-zinc-400 text-xs truncate max-w-[220px]">
                              {o.items.map((it) => it.product_name).join(", ")}
                            </div>
                          </div>
                        ) : (
                          o.product_name
                        )}
                      </td>
                      <td className="p-3 text-[#d4af37]">{o.multi_line ? "—" : o.size}</td>
                      <td className="p-3">{o.quantity}</td>
                      <td className="p-3 font-bold">£{Number(o.amount_total ?? o.amount ?? 0).toFixed(2)}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 text-[9px] tracking-[0.2em] uppercase border ${o.payment_status === "paid" ? "text-[#16a34a] border-[#16a34a]" : "text-zinc-500 border-zinc-700"}`}>
                          {o.payment_status || "pending"}
                        </span>
                      </td>
                      <td className="p-3 text-zinc-400 text-xs">{o.customer_email || "—"}</td>
                      <td className="p-3">
                        <button
                          onClick={() => setExpandedId(expandedId === o.session_id ? null : o.session_id)}
                          className="text-[9px] tracking-[0.2em] uppercase text-zinc-500 hover:text-white"
                        >
                          {expandedId === o.session_id ? "HIDE" : "VIEW"}
                        </button>
                      </td>
                    </tr>
                    {expandedId === o.session_id && (
                      <tr className="border-b border-zinc-800 bg-[#0a0a0a]">
                        <td colSpan={8} className="p-5">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-xs">
                            <div>
                              <div className="text-[9px] tracking-[0.3em] uppercase text-[#d4af37] mb-2">// CUSTOMER</div>
                              <div className="font-mono space-y-1 text-zinc-300">
                                <div>{o.shipping_name || "—"}</div>
                                <div>{o.customer_email || "—"}</div>
                                <div>{o.customer_phone || "—"}</div>
                              </div>
                            </div>
                            <div>
                              <div className="text-[9px] tracking-[0.3em] uppercase text-[#d4af37] mb-2">// SHIP TO</div>
                              <div className="font-mono text-zinc-300">
                                {o.shipping_address ? (
                                  <div className="space-y-0.5">
                                    <div>{o.shipping_address.line1}</div>
                                    {o.shipping_address.line2 && <div>{o.shipping_address.line2}</div>}
                                    <div>{o.shipping_address.city}, {o.shipping_address.state}</div>
                                    <div>{o.shipping_address.postal_code}</div>
                                    <div className="text-[#d4af37]">{o.shipping_address.country}</div>
                                  </div>
                                ) : (
                                  <div className="text-zinc-600">— no address yet —</div>
                                )}
                              </div>
                            </div>
                            <div>
                              <div className="text-[9px] tracking-[0.3em] uppercase text-[#d4af37] mb-2">// BREAKDOWN</div>
                              <div className="font-mono space-y-1 text-zinc-400">
                                {Array.isArray(o.items) && o.items.length > 0 && (
                                  <div className="mb-2 border-b border-zinc-800 pb-2 space-y-0.5">
                                    {o.items.map((it, idx) => (
                                      <div key={idx} className="text-zinc-300">
                                        <span className="text-[#d4af37]">{it.quantity}×</span> {it.product_name}
                                        <span className="text-zinc-600"> · {it.size}</span>
                                        <span className="float-right">£{Number(it.line_total || 0).toFixed(2)}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                <div>Item: £{Number(o.amount_net || o.amount || 0).toFixed(2)}</div>
                                <div>VAT (20%): £{Number(o.amount_vat || 0).toFixed(2)}</div>
                                <div>Shipping: £{Number(o.shipping_cost || 0).toFixed(2)}</div>
                                <div className="text-white font-bold pt-1 border-t border-zinc-800">
                                  Total: £{Number(o.amount_total ?? o.amount ?? 0).toFixed(2)}
                                </div>
                                <div className="text-zinc-600 pt-2">Session: {o.session_id?.slice(0, 20)}...</div>
                                {o.live_mode !== undefined && (
                                  <div className={o.live_mode ? "text-[#d4af37]" : "text-zinc-500"}>
                                    {o.live_mode ? "LIVE MODE" : "TEST MODE"}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminOrders;
